import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { 
  clearPendingTaskDataRequest,
  clearPendingColumnOptionsRequest,
  addSpreadsheetData,
} from '../../lib/redux/sheets/slice';
import { useSendWindowMessages } from './useSendWindowMessages';
import { useSpreadsheetApiCall } from './useSpreadsheetApiCall';

export function useSpreadsheetRequestHandler() {
  const dispatch = useDispatch();
  const { sendColumnOptions, sendTaskData, sendPaginatedTaskData } = useSendWindowMessages();
  const { makeApiCall } = useSpreadsheetApiCall();
  const auth = useSelector((state: RootState) => state.googleSheets.auth);
  const windowMessages = useSelector((state: RootState) => state.googleSheets.windowMessages);
  const spreadsheetData = useSelector((state: RootState) => state.googleSheets.spreadsheetData);
  
  const pendingTaskDataRequest = windowMessages.pendingTaskDataRequest;
  const pendingColumnOptionsRequest = windowMessages.pendingColumnOptionsRequest;
  const tokenConfig = auth.tokenConfig;

  // Helper function to find the correct sheet by gid or fall back to first sheet
  const findSheetByGid = useCallback((sheets: any[], gid?: string) => {
    if (!sheets || sheets.length === 0) return null;
    
    if (gid) {
      const targetSheet = sheets.find(sheet => sheet.properties?.sheetId?.toString() === gid);
      if (targetSheet) return targetSheet;
    }
    
    // Fall back to first sheet
    return sheets[0];
  }, []);

  // Handle task data requests
  const handleTaskDataRequest = useCallback(async (spreadsheetId: string, sheetGid?: string) => {
    try {
      // Check if we have access to this spreadsheet first
      const hasAccess = spreadsheetData.spreadsheetAccess.includes(spreadsheetId);
      if (!hasAccess) {
        sendTaskData(spreadsheetId, []);
        return;
      }

      if (!tokenConfig) {
        sendTaskData(spreadsheetId, []);
        return;
      }

      // First get the full spreadsheet metadata
      const metadataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,properties.title,sheets(properties/gridProperties/rowCount,properties/gridProperties/columnCount,properties/title,properties/sheetId,data/rowMetadata/hiddenByFilter)`;
      const metadataResult = await makeApiCall({ url: metadataUrl });

      if (!metadataResult.success || !metadataResult.data) {
        sendTaskData(spreadsheetId, []);
        return;
      }

      const metadata = metadataResult.data;
      const targetSheet = findSheetByGid(metadata.sheets || [], sheetGid);

      if (!targetSheet) {
        sendTaskData(spreadsheetId, []);
        return;
      }

      const sheetName = targetSheet.properties.title;
      
      // Extract row metadata for filter information
      const rowMetadata = targetSheet.data?.[0]?.rowMetadata || [];

      // Fetch all data from the first sheet (no column limitations)
      const dataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}`;
      const dataResult = await makeApiCall({ url: dataUrl });

      if (!dataResult.success || !dataResult.data) {
        sendTaskData(spreadsheetId, []);
        return;
      }

      const rows = dataResult.data.values || [];

      if (rows.length === 0) {
        sendTaskData(spreadsheetId, []);
        return;
      }

      // Store the full spreadsheet data in Redux for call logs
      const spreadsheetDataForRedux = {
        id: metadata.spreadsheetId,
        name: metadata.properties.title,
        sheets: metadata.sheets.map((sheet: any) => ({
          title: sheet.properties.title,
          sheetId: sheet.properties.sheetId,
          rowCount: sheet.properties.gridProperties?.rowCount || 0,
          columnCount: sheet.properties.gridProperties?.columnCount || 0,
        })),
        data: rows,
      };

      dispatch(addSpreadsheetData(spreadsheetDataForRedux));

      // First row contains headers
      const headers = rows[0];
      
      // Convert remaining rows to objects with headers as keys
      const formattedData = rows.slice(1).map((row: any[], index: number) => {
        const rowNumber = index + 2; // +2 because we skip header row and spreadsheet rows are 1-indexed
        const rowData: Record<string, any> = {
          ROW_NUMBER: rowNumber,
          CONTACT_ID: rowNumber - 2 // 0-based index excluding header
        }
        
        // Check if this row is hidden by filter
        // index + 1 because we're processing data rows (skipping header), but rowMetadata includes header row
        const metadataIndex = index + 1;
        const isHiddenByFilter = rowMetadata[metadataIndex]?.hiddenByFilter === true;
        rowData.hiddenByFilter = isHiddenByFilter;
        
        headers.forEach((header: string, colIndex: number) => {
          if (header && header.trim()) {
            rowData[header.trim()] = row[colIndex] || '';
          }
        });
        
        return rowData;
      });
      sendTaskData(spreadsheetId, formattedData);
    } catch (error) {
      console.error('Error handling task data request:', error);
      sendTaskData(spreadsheetId, []);
    } finally {
      dispatch(clearPendingTaskDataRequest());
    }
  }, [spreadsheetData.spreadsheetAccess, tokenConfig, sendTaskData, dispatch, makeApiCall, findSheetByGid]);

  // Handle paginated task data requests
  const handlePaginatedTaskDataRequest = useCallback(async (spreadsheetId: string, paginationParams: any, sheetGid?: string) => {
    try {
      const { page = 1, page_size = 100 } = paginationParams || {};
      
      // Check if we have access to this spreadsheet first
      const hasAccess = spreadsheetData.spreadsheetAccess.includes(spreadsheetId);
      if (!hasAccess) {
        sendPaginatedTaskData(spreadsheetId, { data: [], hasAdditional: false, totalRows: 0 });
        return;
      }

      if (!tokenConfig) {
        sendPaginatedTaskData(spreadsheetId, { data: [], hasAdditional: false, totalRows: 0 });
        return;
      }

      // Get spreadsheet metadata to get total row count and sheet info
      const metadataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=spreadsheetId,properties.title,sheets(properties/gridProperties/rowCount,properties/gridProperties/columnCount,properties/title,properties/sheetId,data/rowMetadata/hiddenByFilter)`;
      const metadataResult = await makeApiCall({ url: metadataUrl });

      if (!metadataResult.success || !metadataResult.data) {
        sendPaginatedTaskData(spreadsheetId, { data: [], hasAdditional: false, totalRows: 0 });
        return;
      }

      const metadata = metadataResult.data;
      const targetSheet = findSheetByGid(metadata.sheets || [], sheetGid);

      if (!targetSheet) {
        sendPaginatedTaskData(spreadsheetId, { data: [], hasAdditional: false, totalRows: 0 });
        return;
      }

      const sheetName = targetSheet.properties.title;
      const totalRows = targetSheet.properties.gridProperties?.rowCount || 0;
      
      // Extract row metadata for filter information
      const rowMetadata = targetSheet.data?.[0]?.rowMetadata || [];
      
      // Calculate range for this page (accounting for header row)
      const startRow = page === 1 ? 1 : ((page - 1) * page_size) + 2; // Page 1 includes header, subsequent pages start after previous page's last row
      const endRow = page === 1 ? page_size + 1 : (page * page_size) + 1; // +1 for header row offset
      
      // Fetch data for the specific range
      const range = `${encodeURIComponent(sheetName)}!${startRow}:${endRow}`;
      const dataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}`;
      const dataResult = await makeApiCall({ url: dataUrl });

      if (!dataResult.success || !dataResult.data) {
        sendPaginatedTaskData(spreadsheetId, { data: [], hasAdditional: false, totalRows });
        return;
      }

      const rows = dataResult.data.values || [];
      
      if (rows.length === 0) {
        sendPaginatedTaskData(spreadsheetId, { data: [], hasAdditional: false, totalRows });
        return;
      }

      let formattedData: any[] = [];
      let headers: string[] = [];
      
      if (page === 1) {
        // First page includes headers
        headers = rows[0] || [];
        const dataRows = rows.slice(1);
        formattedData = dataRows.map((row: any[], index: number) => {
          const rowNumber = index + 2; // +2 because we skip header row and spreadsheet rows are 1-indexed
          const rowData: Record<string, any> = {
            ROW_NUMBER: rowNumber,
            CONTACT_ID: rowNumber - 2 // 0-based index excluding header
          };
          
          // Check if this row is hidden by filter
          // index + 1 because we're processing data rows (skipping header), but rowMetadata includes header row
          const metadataIndex = index + 1;
          const isHiddenByFilter = rowMetadata[metadataIndex]?.hiddenByFilter === true;
          rowData.hiddenByFilter = isHiddenByFilter;
          
          headers.forEach((header: string, colIndex: number) => {
            if (header && header.trim()) {
              rowData[header.trim()] = row[colIndex] || '';
            }
          });
          
          return rowData;
        });
      } else {
        // For subsequent pages, we need to get headers separately
        const headerUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}!1:1`;
        const headerResult = await makeApiCall({ url: headerUrl });
        
        if (!headerResult.success || !headerResult.data) {
          sendPaginatedTaskData(spreadsheetId, { data: [], hasAdditional: false, totalRows });
          return;
        }
        
        headers = headerResult.data.values?.[0] || [];
        
        formattedData = rows.map((row: any[], index: number) => {
          const rowNumber = startRow + index; // Correct row number for this page
          const rowData: Record<string, any> = {
            ROW_NUMBER: rowNumber,
            CONTACT_ID: rowNumber - 2 // 0-based index excluding header
          };
          
          // Check if this row is hidden by filter
          // For subsequent pages, we need to calculate the correct metadata index
          const actualRowIndex = startRow + index - 1; // Convert to 0-based index
          const isHiddenByFilter = rowMetadata[actualRowIndex]?.hiddenByFilter === true;
          rowData.hiddenByFilter = isHiddenByFilter;
          
          headers.forEach((header: string, colIndex: number) => {
            if (header && header.trim()) {
              rowData[header.trim()] = row[colIndex] || '';
            }
          });
          
          return rowData;
        });
      }

      // Calculate if there are more pages
      const hasAdditional = endRow < totalRows;
      
      sendPaginatedTaskData(spreadsheetId, { 
        data: formattedData, 
        hasAdditional, 
        totalRows,
        currentPage: page,
        pageSize: page_size
      });
    } catch (error) {
      console.error('Error handling paginated task data request:', error);
      sendPaginatedTaskData(spreadsheetId, { data: [], hasAdditional: false, totalRows: 0 });
    } finally {
      dispatch(clearPendingTaskDataRequest());
    }
  }, [spreadsheetData.spreadsheetAccess, tokenConfig, sendPaginatedTaskData, dispatch, makeApiCall, findSheetByGid]);

  // Handle column options requests
  const handleColumnOptionsRequest = useCallback(async (spreadsheetId: string, sheetGid?: string) => {
    try {
      // Check if we have access to this spreadsheet first
      const hasAccess = spreadsheetData.spreadsheetAccess.includes(spreadsheetId);
      if (!hasAccess) {
        sendColumnOptions(spreadsheetId, []);
        return;
      }

      if (!tokenConfig) {
        sendColumnOptions(spreadsheetId, []);
        return;
      }

      // Get spreadsheet metadata to find the actual sheet name
      const metadataUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets.properties`;
      const metadataResult = await makeApiCall({ url: metadataUrl });

      if (!metadataResult.success || !metadataResult.data) {
        sendColumnOptions(spreadsheetId, []);
        return;
      }

      const targetSheet = findSheetByGid(metadataResult.data.sheets || [], sheetGid);
      
      if (!targetSheet) {
        sendColumnOptions(spreadsheetId, []);
        return;
      }

      const sheetName = targetSheet.properties?.title;
      if (!sheetName) {
        sendColumnOptions(spreadsheetId, []);
        return;
      }

      // Get the header row
      const headerUrl = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}!1:1`;
      const headerResult = await makeApiCall({ url: headerUrl });

      if (!headerResult.success || !headerResult.data) {
        sendColumnOptions(spreadsheetId, []);
        return;
      }

      const headers = headerResult.data.values?.[0] || [];
      
      // Filter out empty headers and send column options
      const columnLabels = headers
        .map((header: string) => header?.trim())
        .filter((header: string) => header && header.length > 0);
      
      sendColumnOptions(spreadsheetId, columnLabels);
    } catch (error) {
      sendColumnOptions(spreadsheetId, []);
    } finally {
      dispatch(clearPendingColumnOptionsRequest());
    }
  }, [spreadsheetData.spreadsheetAccess, tokenConfig, sendColumnOptions, dispatch, makeApiCall, findSheetByGid]);

  // Handle pending task data requests
  useEffect(() => {
    if (!pendingTaskDataRequest || !tokenConfig) return;
    
    if (pendingTaskDataRequest.pagination) {
      // Handle paginated request
      handlePaginatedTaskDataRequest(pendingTaskDataRequest.spreadsheetId, pendingTaskDataRequest.pagination, pendingTaskDataRequest.sheetGid);
    } else {
      // Handle regular request
      handleTaskDataRequest(pendingTaskDataRequest.spreadsheetId, pendingTaskDataRequest.sheetGid);
    }
  }, [pendingTaskDataRequest, tokenConfig, handleTaskDataRequest, handlePaginatedTaskDataRequest]);

  // Handle pending column options requests
  useEffect(() => {
    if (!pendingColumnOptionsRequest || !tokenConfig) return;
    handleColumnOptionsRequest(pendingColumnOptionsRequest.spreadsheetId, pendingColumnOptionsRequest.sheetGid);
  }, [pendingColumnOptionsRequest, tokenConfig, handleColumnOptionsRequest]);
} 